/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { IconButton, Typography, Modal, Box } from "@mui/material";
import "./DetailsPopup.css";
import Grid from "@mui/material/Grid2";
import styles from "../../../page/ApplicationList/ApplicationList.module.css";
import { ClearIcon } from "@mui/x-date-pickers";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TaxTablePopUp from "../TaxTablePopUp/TaxTablePopUp";

function createDataPlan(
  subscriptionFrequency,
  subscriptionFrequencyUnit,
  price,
  currencyCode,
  planId,
  productId
) {
  return {
    subscriptionFrequency,
    subscriptionFrequencyUnit,
    price,
    currencyCode,
    planId,
    productId,
  };
}

function DetailsPopup({ onClose, productData }) {
  const [productDetails, setProductDetails] = React.useState([]);
  const [plans, setPlans] = useState([]);
  const [showTaxDetailsModal, setShowTaxDetailsModal] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState("");
  const [taxModalType, setTaxModalType] = React.useState("");

  const frequencyUnitConverter = {
    "1-DAY": "Daily",
    "1-MONTH": "Monthly",
    "3-MONTH": "Quarterly",
    "6-MONTH": "Half yearly",
    "1-YEAR": "Yearly",
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: "4px",
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };

  useEffect(() => {
    if (productData?.id) {
      APIManager.getProductDetails(
        productData.id,
        productData.type === "recurring"
      ).then((json) => {
        setProductDetails(json);
        if (
          productData.type === "recurring" &&
          json.recurringPlans?.length > 0
        ) {
          let allRows = [];
          json.recurringPlans.forEach((plan) => {
            allRows.push(
              createDataPlan(
                plan.recurringFrequency,
                plan.recurringFrequencyUnit,
                plan.basePrice.price,
                plan.basePrice.currencyCode,
                plan.id,
                json.product.id
              )
            );
          });
          setPlans(allRows);
        }
      });
    }
  }, [productData]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.action.hover,
      fontWeight: "600",
      border: 0,
      paddingTop: 8,
      paddingBottom: 8,
      borderBottom: "1px solid #d0d0d0 !important",
      fontSize: 12,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "td, th": {
      border: 0,
      paddingTop: 5,
      paddingBottom: 5,
      borderBottom: "1px solid #e8e8e8",
    },
  }));

  const openTaxDetailsModal = () => {
    setShowTaxDetailsModal(true);
  };

  const handleCloseTaxDetailsPopup = () => {
    setShowTaxDetailsModal(false);
  };

  return (
    <>
      <div className={styles.modalHeaderCls}>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          In-App Products Pricing
        </Typography>
        <IconButton>
          {" "}
          <ClearIcon onClick={onClose} sx={{ padding: "none" }} />
        </IconButton>
      </div>
      <div style={{marginTop: "3%"}}>
        {productDetails?.product &&
          productData.type === "recurring" && (
            <div>
              <Grid container spacing={1}>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Product Id:
                    </span>
                    <span className="product-details-modal-info-value">
                      {productDetails.product.productId}
                    </span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Plan Name:
                    </span>
                    <span className="product-details-modal-info-value">{productDetails.product.name}</span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Trial Period:
                    </span>
                    <span className="product-details-modal-info-value">
                      {productDetails.trialPrice?.trialPeriod && (
                          <>
                            {productDetails.trialPrice.trialPeriod} Day
                            {productDetails.trialPrice.trialPeriod > 1 && (
                              <>s</>
                            )}
                          </>
                        )}
                      {!productDetails.trialPrice && <>0 Day</>}
                    </span>
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    <span className="product-details-modal-info-label">
                      Recurrence:
                    </span>
                    <span className="product-details-modal-info-value">Enabled</span>
                  </Typography>
                </Grid>
              </Grid>
              <div>
                <Typography variant="subtitle2" gutterBottom>
                  Plans
                </Typography>
                {plans.length > 0 && (
                  <TableContainer>
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>
                            Subscription Frequency
                          </StyledTableCell>
                          <StyledTableCell>Price</StyledTableCell>
                          <StyledTableCell>Offers</StyledTableCell>
                          <StyledTableCell></StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {plans.map((plan) => (
                          <StyledTableRow key={plan.planId}>
                            <StyledTableCell
                              sx={{ fontWeight: "bold", width: "30%" }}
                            >
                              {
                                frequencyUnitConverter[
                                  plan.subscriptionFrequency +
                                    "-" +
                                    plan.subscriptionFrequencyUnit
                                ]
                              }
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: "20%" }}>
                              <span
                                style={{ color: "#717273", marginRight: "10%" }}
                              >
                                USD
                              </span>
                              {plan.price}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: "45%" }}>
                              ---
                            </StyledTableCell>
                            <StyledTableCell>
                              <ArrowForwardIcon
                                className="edit-icon"
                                onClick={() => {
                                  setTaxModalType("InAppPlan");
                                  setSelectedProduct(plan);
                                  openTaxDetailsModal();
                                }}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </div>
            </div>
          )}
        <Modal
          open={showTaxDetailsModal}
          aria-labelledby="parent-modal-title"
          aria-describedby="parent-modal-description"
        >
          <Box
            sx={{ ...style, width: 750, maxHeight: 500, overflow: "scroll" }}
          >
            <TaxTablePopUp
              onClose={handleCloseTaxDetailsPopup}
              productData={selectedProduct}
              type={taxModalType}
            />
          </Box>
        </Modal>
      </div>
    </>
  );
}

DetailsPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  productData: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
};

export default DetailsPopup;
